import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth/AuthProvider";
import type { MentorProfile, MentorCheck } from "./types";

/**
 * Read & upsert the current user's mentor profile + most recent mentor_check.
 * Used by the "Apply to be a mentor" / "Edit mentor profile" page.
 */
export const useMentorProfile = () => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<MentorProfile | null>(null);
  const [check, setCheck] = useState<MentorCheck | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!user) {
      setProfile(null);
      setCheck(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    const [{ data: prof, error: pErr }, checkRes] = await Promise.all([
      supabase
        .from("mentor_profiles")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle(),
      supabase
        .from("mentor_checks")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle(),
    ]);

    if (pErr) setError(pErr.message);
    setProfile((prof as MentorProfile | null) ?? null);
    setCheck((checkRes.data as MentorCheck | null) ?? null);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    void load();
  }, [load]);

  const upsert = useCallback(
    async (patch: Partial<MentorProfile> & { app: string }) => {
      if (!user) return { error: "Not signed in" };
      const row = { user_id: user.id, ...patch };
      const { data, error } = await supabase
        .from("mentor_profiles")
        .upsert(row as any, { onConflict: "user_id,app" })
        .select("*")
        .maybeSingle();
      if (error) return { error: error.message };
      setProfile((data as MentorProfile | null) ?? null);

      // Create / refresh a pending mentor_check if none active.
      if (!check || check.status === "rejected") {
        await supabase.from("mentor_checks").insert({
          user_id: user.id,
          status: "pending",
        } as any);
        await load();
      }
      return { data };
    },
    [user, check, load],
  );

  return { profile, check, loading, error, reload: load, upsert };
};
